import React, { useEffect }                                                            from 'react';
import { PluginDocumentSettingPanel }                                                  from '@wordpress/edit-post';
import {
    CheckboxControl,
    SelectControl,
} from '@wordpress/components';
import { useDispatch }                                                                       from '@wordpress/data';
import { store as noticesStore }                                                             from '@wordpress/notices';
import { __ }                                                                                from '@wordpress/i18n';
import withMetaboxValidation                                                                 from './withMetaboxValidation';
import { OrderBumpsMeta, ValidationRules }                                                   from '../../Types/ValidationRules';

declare const cfwOrderBumpsData: {
    supportedGateways?: string[];
};

const getValidationRules: ( meta: OrderBumpsMeta ) => ValidationRules = ( meta ) => ( {
} );

const OrderBumpsDisplayLocation = ( { meta, handleFieldChange } ) => {
    const { createNotice, removeNotice } = useDispatch( noticesStore );

    useEffect( () => {
        if ( ![ 'complete_order', 'post_purchase_one_click' ].includes( meta.cfw_ob_display_location )
            || (
                meta.cfw_ob_full_screen !== 'yes'
                && [ 'complete_order', 'post_purchase_one_click' ].includes( meta.cfw_ob_display_location )
            )
        ) {
            createNotice(
                'warning',
                __( 'Block Editor is only used for Full Screen Order Bumps (Configurable for Place Order and Post Purchase bumps). Blocks are ignored for other configurations.', 'checkout-wc' ),
                { id: 'notice-cfw_ob_display_location', isDismissible: false },
            );
        } else {
            removeNotice( 'notice-cfw_ob_display_location' );
        }

        return () => {
            removeNotice( 'notice-cfw_ob_display_location' );
        };
    }, [ meta.cfw_ob_display_location, meta.cfw_ob_full_screen ] );

    useEffect( () => {
        if ( meta.cfw_ob_display_location === 'post_purchase_one_click' && !cfwOrderBumpsData?.supportedGateways?.length ) {
            createNotice(
                'error',
                __( 'Post Purchase bumps require a compatible payment gateway. We recommend using Place Order bumps when using non-compatible gateways.', 'checkout-wc' ),
                {
                    id: 'notice-cfw_ob_post_purchase_gateway',
                    isDismissible: false,
                    actions: [
                        {
                            label: __( 'Learn more about supported gateways', 'checkout-wc' ),
                            url: 'https://www.checkoutwc.com/documentation/supported-one-click-gateways/',
                            onClick: null,
                        },
                        {
                            label: __( 'Read our Order Bumps documentation', 'checkout-wc' ),
                            url: 'https://www.checkoutwc.com/documentation/order-bumps/',
                            onClick: null,
                        },
                    ],
                },
            );
        } else {
            removeNotice( 'notice-cfw_ob_post_purchase_gateway' );
        }

        return () => {
            removeNotice( 'notice-cfw_ob_post_purchase_gateway' );
        };
    }, [ meta.cfw_ob_display_location ] );

    return (
        <PluginDocumentSettingPanel
            name="cfw-ob-display-location"
            title={__( 'Display Location', 'checkout-wc' )}
            className="cfw-ob-display-location-mb"
        >
            <div
                style={{ marginBottom: '1rem' }}
            >
                <SelectControl
                    value={meta.cfw_ob_display_location}
                    options={[
                        { label: __( 'Below Cart Items', 'checkout-wc' ), value: 'below_cart_items' },
                        { label: __( 'Below Cart Items (Side Cart Only)', 'checkout-wc' ), value: 'below_side_cart_items' },
                        { label: __( 'Below Cart Items (Checkout Only)', 'checkout-wc' ), value: 'below_checkout_cart_items' },
                        { label: __( 'Above Terms and Conditions', 'checkout-wc' ), value: 'above_terms_and_conditions' },
                        { label: __( 'Above Express Checkout', 'checkout-wc' ), value: 'above_express_checkout' },
                        { label: __( 'Bottom of Information Step', 'checkout-wc' ), value: 'bottom_information_tab' },
                        { label: __( 'Bottom of Shipping Step', 'checkout-wc' ), value: 'bottom_shipping_tab' },
                        { label: __( 'Below Complete Order Button', 'checkout-wc' ), value: 'below_complete_order_button' },
                        { label: __( 'Place Order Click (Checkout)', 'checkout-wc' ), value: 'complete_order' },
                        { label: __( 'Post Purchase (Thank You)', 'checkout-wc' ), value: 'post_purchase_one_click' },
                    ]}
                    onChange={
                        ( newValue: any ) => {
                            handleFieldChange( 'cfw_ob_display_location', newValue );
                        }
                    }
                />
                {[ 'below_cart_items', 'below_checkout_cart_items' ].includes( meta.cfw_ob_display_location )
                    && <><p>{__( 'Note: Displays above the terms and conditions on mobile devices.', 'checkout-wc' )}</p></>
                }
                {[ 'complete_order' ].includes( meta.cfw_ob_display_location )
                    && <><p>{__( 'Displays in a popup when Place Order is clicked, before the order is submitted', 'checkout-wc' )}</p></>
                }
                {[ 'post_purchase_one_click' ].includes( meta.cfw_ob_display_location )
                    && <><p>{__( 'Displays in a popup on the Thank You page.', 'checkout-wc' )}</p></>
                }
                {[ 'complete_order', 'post_purchase_one_click' ].includes( meta.cfw_ob_display_location )
                    && <CheckboxControl
                        label={__( 'Full Screen Landing Page (Use Block Editor To Design)', 'checkout-wc' )}
                        help={__( 'If checked, the bump offer will be displayed full screen. Use Order Bump Offer Form block to design the offer.', 'checkout-wc' )}
                        checked={meta.cfw_ob_full_screen === 'yes'}
                        onChange={
                            ( newValue: boolean ) => {
                                handleFieldChange( 'cfw_ob_full_screen', newValue ? 'yes' : 'no' );
                            }
                        }
                    />
                }
            </div>
        </PluginDocumentSettingPanel>
    );
};

export default withMetaboxValidation( OrderBumpsDisplayLocation, getValidationRules );
